import { CommandCenterHub } from "./hub/CommandCenterHub";
import { buildDivisions, buildQuickAccess } from "./hub/commandCenterHubModel";
import type {
  CommandCenterHubAction,
  CommandResult,
  HubRoute,
} from "./hub/CommandCenterHub.types";

/**
 * SquareCodeCommandCenter — the Square Code V2 Command Center body.
 *
 * This is the approved Final Hub Center: an icon-first, neon-on-black console
 * (SQ monogram + live clock, Quick Access, ten Divisions, a neon command dock
 * with a six-tool canvas). It is PRESENTATION ONLY and SSR-safe — it holds no
 * hooks and performs no provider work. Every real action is supplied by the
 * route wrapper (SquareCodeCommandCenterRoute) through onNavigate / onCommand
 * / onTool, which are wired to the repository's router, command dispatcher,
 * upload service, and Daily/media handlers.
 *
 * Defaults render the tiles from the same typed division data the rest of the
 * command center uses, so rendering the component alone (e.g. in an SSR test)
 * still paints the real, navigable hub.
 */
export interface SquareCodeCommandCenterProps {
  /** Quick Access tiles (Divisions / Projects / Notifications / Calendar). */
  quickAccess?: HubRoute[];
  /** The ten Division tiles. */
  divisions?: HubRoute[];
  /** Navigate to a real route. */
  onNavigate?: (path: string, id: string) => void;
  /** Dispatch a free-text command through the real command router. */
  onCommand?: (command: string) => Promise<CommandResult>;
  /** Run one of the six tool actions through the real repository handler. */
  onTool?: (action: CommandCenterHubAction) => Promise<CommandResult>;
}

const noopNavigate = () => {};
const unavailableCommand = async (): Promise<CommandResult> => ({
  ok: false,
  reason: "Command routing is not available here.",
});
const unavailableTool = async (): Promise<CommandResult> => ({
  ok: false,
  reason: "This tool is not available here.",
});

export default function SquareCodeCommandCenter({
  quickAccess,
  divisions,
  onNavigate = noopNavigate,
  onCommand = unavailableCommand,
  onTool = unavailableTool,
}: SquareCodeCommandCenterProps) {
  return (
    <CommandCenterHub
      quickAccess={quickAccess ?? buildQuickAccess()}
      divisions={divisions ?? buildDivisions()}
      onNavigate={onNavigate}
      onCommand={onCommand}
      onTool={onTool}
    />
  );
}
